<script setup lang="ts">
import { computed } from 'vue';
import { UiCard, UiNavLink, UiSquareButton, UiText } from '@shopexperts/shared';
import type { PageSchema } from '@shopexperts/shared';

const props = defineProps<{
  schema: PageSchema;
}>();

const emit = defineEmits<{
  editSchema: [];
  deleteSchema: [];
}>();

/** The row itself opens the page editor; a real href keeps open-in-new-tab working. */
const href = computed(() => `/admin/pages/${props.schema.id}/editor`);
const fieldCount = computed(() => props.schema.fields?.length ?? 0);
const pagesCount = computed(() => props.schema.pages_count ?? 0);
const updatedLabel = computed(() => {
  if (!props.schema.updated_at) return 'never';
  const date = new Date(props.schema.updated_at);
  if (Number.isNaN(date.getTime())) return 'never';
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
});
const summary = computed(() => [
  `${fieldCount.value} ${fieldCount.value === 1 ? 'field' : 'fields'}`,
  `${pagesCount.value} ${pagesCount.value === 1 ? 'page' : 'pages'}`,
  `updated ${updatedLabel.value}`,
].join(' · '));
</script>

<template>
  <UiCard hover="border" padding="md" class="relative">
    <!-- Own overlay link rather than UiCard's: it needs a name of its own, and
         the action buttons have to sit above it to stay clickable. -->
    <UiNavLink
      :to="href"
      class="absolute inset-0 z-10"
      :aria-label="`Edit the pages in ${props.schema.label}`"
    />

    <div class="flex items-center justify-between gap-4">
      <div class="min-w-0">
        <div class="flex min-w-0 items-center gap-2">
          <UiText as="h3" variant="h6" class="truncate font-semibold text-se-dark" :text="props.schema.label" />
          <span class="shrink-0 rounded bg-se-gray-100 px-2 py-0.5 font-mono text-xs text-se-gray-700">
            {{ props.schema.name }}
          </span>
        </div>
        <UiText
          v-if="props.schema.description"
          as="p"
          variant="p-small"
          class="mt-1 line-clamp-1 text-se-gray-700"
          :text="props.schema.description"
        />
        <UiText as="p" variant="caption-normal" class="mt-1 text-se-gray-700" :text="summary" />
      </div>

      <div class="relative z-20 flex shrink-0 items-center gap-1">
        <UiSquareButton
          icon="pencil"
          variant="ghost"
          :aria-label="`Edit the ${props.schema.label} schema`"
          title="Edit schema"
          @click.prevent.stop="emit('editSchema')"
        />
        <UiSquareButton
          icon="delete"
          variant="ghost"
          class="hover:text-red-600"
          :aria-label="`Delete the ${props.schema.label} schema`"
          title="Delete schema"
          @click.prevent.stop="emit('deleteSchema')"
        />
      </div>
    </div>
  </UiCard>
</template>
